// ============= Imports =============
import { toastHistoryStore } from '@/components/common/Toast/ToastHistory';

// ============= Logging Utility =============

export interface RelayHubLogMeta {
  workspaceId?: string;
  displayName?: string;
}

/**
 * Creates a log function that writes to console and to the log history (not toasts).
 * Shared across workspace, user, and ask relay hubs. Raw SignalR events go here;
 * they are never shown as user-facing toasts—only in the Toast/Log panel as "Log".
 *
 * @param hubName - Hub name (e.g. 'WorkspaceRelayHub', 'UserRelayHub', 'AskRelayHub').
 * @param enableLogging - When false, the returned function is a no-op.
 * @param emoji - Optional emoji for console output only (e.g. '📁', '👤', '💬').
 * @returns A function (event, data, meta?) => void that logs and persists as a log entry.
 */
export const createRelayHubLog = (
  hubName: string,
  enableLogging: boolean,
  emoji?: string
): (event: string, data: unknown, meta?: RelayHubLogMeta) => void => {
  const consolePrefix = emoji ? `${emoji} [${hubName}]` : `[${hubName}]`;
  return (event: string, data: unknown, meta?: RelayHubLogMeta) => {
    if (!enableLogging) {
      return;
    }

    console.log(`${consolePrefix} ${event}:`, data);

    try {
      toastHistoryStore.addLogEntry({
        type: 'info',
        message: `[${hubName}] ${event}`,
        direction: 'incoming',
        source: (() => {
          try {
            return JSON.stringify(data, null, 2);
          } catch {
            return String(data);
          }
        })(),
        ...(meta && {
          workspaceId: meta.workspaceId,
          displayName: meta.displayName,
        }),
      });
    } catch {
      // Never let logging break the app
    }
  };
}
